import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { BookOpen, ChevronDown, FileText, FileSpreadsheet, ExternalLink, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../services/api';
import { formatDateDisplay } from '../lib/dateUtils';

export default function ProfessionalLedger() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [voucherDetail, setVoucherDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    accountingAPI.chartOfAccounts({}).then(r => {
      const leaf = (r.data || []).filter(a => !a.is_group);
      setAccounts(leaf);
    }).catch(() => toast.error('Failed to load accounts'));
  }, []);

  const loadLedger = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params = { page };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await accountingAPI.accountLedger(selectedAccount, params);
      setLedger(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [selectedAccount, dateFrom, dateTo, page]);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const selectedName = accounts.find(a => a.account_code === selectedAccount)?.account_name || '';

  const downloadFile = async (format) => {
    if (!selectedAccount) return;
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const url = `/accounting/ledger/${selectedAccount}/${format}?${params.toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ledger_${selectedAccount}.${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch { toast.error(`Failed to download ${format.toUpperCase()}`); }
  };

  const openVoucherDetail = async (row) => {
    // For invoice-backed vouchers, navigate directly to the invoice detail page.
    if ((row.voucher_type === 'sales' || row.voucher_type === 'purchase') && row.source_id) {
      navigate(`/invoices/${row.source_id}`);
      return;
    }
    const targetId = row.entry_id || row.source_id;
    if (!targetId) {
      toast.error('No detail link available for this row');
      return;
    }
    setDetailLoading(true);
    try {
      const res = await accountingAPI.getJournalEntry(targetId);
      setVoucherDetail(res.data);
    } catch {
      toast.error('Could not load voucher details');
    }
    finally { setDetailLoading(false); }
  };

  const downloadVoucher = async () => {
    if (!voucherDetail?.voucher_number) return;
    try {
      const params = new URLSearchParams();
      params.append('date_from', voucherDetail.date);
      params.append('date_to', voucherDetail.date);
      const acctCode = voucherDetail.lines?.[0]?.account_code || selectedAccount;
      const url = `/accounting/ledger/${acctCode}/pdf?${params.toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `voucher_${voucherDetail.voucher_number}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Voucher downloaded');
    } catch { toast.error('Failed to download voucher'); }
  };

  return (
    <div data-testid="professional-ledger-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div></div>
        {selectedAccount && ledger && (
          <div className="flex items-center gap-2">
            <button onClick={() => downloadFile('pdf')} data-testid="download-ledger-pdf"
              className="btn-secondary flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-red-500" /> PDF
            </button>
            <button onClick={() => downloadFile('excel')} data-testid="download-ledger-excel"
              className="btn-secondary flex items-center gap-2 text-sm">
              <FileSpreadsheet className="w-4 h-4 text-green-600" /> Excel
            </button>
          </div>
        )}
      </div>

      {/* Filters Row */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Select Account</label>
            <div className="relative">
              <select value={selectedAccount}
                onChange={e => { setSelectedAccount(e.target.value); setPage(1); }}
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                data-testid="ledger-account-select">
                <option value="">-- Choose Account --</option>
                {accounts.map(a => (
                  <option key={a.account_code} value={a.account_code}>
                    {a.account_code} — {a.account_name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">From</label>
            <input type="date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              data-testid="ledger-date-from" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">To</label>
            <input type="date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              data-testid="ledger-date-to" />
          </div>
        </div>
      </div>

      {/* Content */}
      {!selectedAccount ? (
        <div className="bg-white border border-slate-200 rounded-lg flex flex-col items-center justify-center py-24">
          <BookOpen className="w-14 h-14 text-slate-300 mb-4" strokeWidth={1.5} />
          <p className="text-slate-400 text-sm">Select an account to view its ledger</p>
        </div>
      ) : loading ? (
        <div className="bg-white border border-slate-200 rounded-lg flex justify-center py-24">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : ledger ? (
        <div className="space-y-4">
          {/* Account Summary */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-bold text-lg text-slate-900">{ledger.account?.account_name || selectedName}</h2>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  {ledger.account?.account_code} | {ledger.account?.account_type} | Normal: {ledger.account?.normal_balance}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Closing Balance</p>
                <p className={`text-2xl font-bold font-mono ${ledger.closing_balance >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>
                  Rs.{formatCurrency(Math.abs(ledger.closing_balance))}
                  <span className="text-xs ml-1.5 font-medium">{ledger.closing_balance >= 0 ? 'Dr' : 'Cr'}</span>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wide">Total Debit</p>
                <p className="text-lg font-bold font-mono text-red-700 mt-0.5">Rs.{formatCurrency(ledger.total_debit)}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Total Credit</p>
                <p className="text-lg font-bold font-mono text-emerald-700 mt-0.5">Rs.{formatCurrency(ledger.total_credit)}</p>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Transactions</p>
                <p className="text-lg font-bold font-mono text-slate-800 mt-0.5">{ledger.total_rows}</p>
              </div>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ledger-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">S.No.</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Voucher</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Narration</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Debit</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Credit</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Running Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(ledger.ledger || []).map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`ledger-row-${idx}`}
                      onClick={() => openVoucherDetail(row)}>
                      <td className="px-4 py-3 text-sm text-slate-400">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDateDisplay(row.date)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[hsl(221,100%,50%)] hover:underline flex items-center gap-1">{row.voucher_number} <ExternalLink className="w-3 h-3 opacity-40" /></td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{row.voucher_type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-[240px] truncate">{row.narration || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {row.debit > 0 ? <span className="text-red-600 font-medium">Rs.{formatCurrency(row.debit)}</span> : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm">
                        {row.credit > 0 ? <span className="text-emerald-600 font-medium">Rs.{formatCurrency(row.credit)}</span> : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm font-bold">
                        <span className={row.balance >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                          Rs.{formatCurrency(Math.abs(row.balance))} {row.balance >= 0 ? 'Dr' : 'Cr'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!ledger.ledger || ledger.ledger.length === 0) && (
                    <tr><td colSpan={8} className="text-center text-slate-400 py-16 text-sm">No transactions in this account</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {ledger.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-sm text-slate-500">Page {page} of {ledger.pages} ({ledger.total_rows} entries)</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-md hover:bg-white transition disabled:opacity-40">Previous</button>
                  <button onClick={() => setPage(p => Math.min(ledger.pages, p + 1))} disabled={page >= ledger.pages}
                    className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-md hover:bg-white transition disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Voucher Detail Dialog */}
      <Dialog open={!!voucherDetail} onOpenChange={(o) => !o && setVoucherDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold tracking-tight flex items-center gap-2">
              Voucher: {voucherDetail?.voucher_number}
            </DialogTitle>
          </DialogHeader>
          {voucherDetail && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="form-label">Date</span><p className="font-medium">{voucherDetail.date}</p></div>
                <div><span className="form-label">Type</span><p className="font-medium capitalize">{voucherDetail.voucher_type}</p></div>
                {voucherDetail.party_name && <div><span className="form-label">Party</span><p className="font-medium">{voucherDetail.party_name}</p></div>}
                {voucherDetail.narration && <div className="col-span-2"><span className="form-label">Narration</span><p className="text-sm">{voucherDetail.narration}</p></div>}
              </div>
              {voucherDetail.lines?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full data-table" data-testid="voucher-lines-table">
                    <thead><tr><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                    <tbody>
                      {voucherDetail.lines.map((line, i) => (
                        <tr key={i}>
                          <td className="text-sm">{line.account_name || line.account_code}</td>
                          <td className="text-right font-mono text-sm">{line.debit > 0 ? `Rs.${formatCurrency(line.debit)}` : '-'}</td>
                          <td className="text-right font-mono text-sm">{line.credit > 0 ? `Rs.${formatCurrency(line.credit)}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={downloadVoucher} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="download-voucher-pdf">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
                <button onClick={() => { setVoucherDetail(null); navigate('/voucher-entries'); }} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="go-to-voucher">
                  <ExternalLink className="w-3.5 h-3.5" /> Open in Journal
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
